// Client-side room state: the local board copy, peers, cursors, and the offline outbox.
// No DOM access here, so the reconnect and replay rules can be unit tested.

import type { Peer } from "../shared/identity.ts";
import { Board, type Stroke } from "../shared/board.ts";
import type { ClientMessage, ServerMessage, StrokeInput } from "../shared/protocol.ts";

export interface Cursor {
  x: number;
  y: number;
}

interface CurrentStroke {
  id: string;
  /** Connection epoch the stroke started on. A different epoch means the server missed part of it. */
  epoch: number;
  unsent: number[];
}

export class ClientSync {
  readonly board = new Board();
  readonly peers = new Map<string, Peer>();
  readonly cursors = new Map<string, Cursor>();
  me: Peer | undefined;
  online = false;
  private epoch = 0;
  private outbox: ClientMessage[] = [];
  private current: CurrentStroke | undefined;

  constructor(
    readonly owner: string,
    private readonly send: (msg: ClientMessage) => void,
    private readonly makeId: () => string = randomId,
  ) {}

  get pendingCount(): number {
    return this.outbox.length;
  }

  get drawing(): boolean {
    return this.current !== undefined;
  }

  disconnected(): void {
    this.online = false;
    this.peers.clear();
    this.cursors.clear();
  }

  beginStroke(color: string, size: number, x: number, y: number): string {
    if (this.current) this.endStroke();
    const id = this.makeId();
    this.board.add({ id, owner: this.owner, color, size, points: [x, y], done: false });
    this.current = { id, epoch: this.epoch, unsent: [] };
    if (this.online) this.send({ t: "start", stroke: { id, color, size, points: [x, y] } });
    return id;
  }

  extendStroke(x: number, y: number): void {
    const current = this.current;
    if (!current) return;
    if (this.board.append(current.id, this.owner, [x, y])) current.unsent.push(x, y);
  }

  /** Sends buffered points. The UI calls this once per animation frame. */
  flushPoints(): void {
    const current = this.current;
    if (!current || current.unsent.length === 0) return;
    if (this.online && current.epoch === this.epoch) {
      this.send({ t: "points", id: current.id, points: current.unsent });
    }
    current.unsent = [];
  }

  endStroke(): void {
    const current = this.current;
    if (!current) return;
    this.flushPoints();
    this.current = undefined;
    const stroke = this.board.finish(current.id, this.owner);
    if (!stroke) return;
    // The finished stroke always goes out whole. The server upserts it, so this covers
    // a connection that dropped mid-stroke and a room that hibernated while the pointer was still.
    const input: StrokeInput = { id: stroke.id, color: stroke.color, size: stroke.size, points: stroke.points.slice() };
    this.sendOrQueue({ t: "add", stroke: input });
  }

  /** Online, the server decides which stroke goes and echoes a remove. Offline, undo applies locally and is queued. */
  undo(): void {
    if (this.current) return;
    if (this.online) {
      this.send({ t: "undo" });
      return;
    }
    if (this.board.undo(this.owner)) this.outbox.push({ t: "undo" });
  }

  clear(): void {
    this.current = undefined;
    if (this.online) {
      this.send({ t: "clear" });
      return;
    }
    this.board.clear();
    this.outbox.push({ t: "clear" });
  }

  sendCursor(x: number, y: number): void {
    if (this.online) this.send({ t: "cursor", x, y });
  }

  hideCursor(): void {
    if (this.online) this.send({ t: "cursor-leave" });
  }

  /** Applies a server message. Returns true when the board itself changed. */
  receive(msg: ServerMessage): boolean {
    switch (msg.t) {
      case "welcome":
        this.welcome(msg.you, msg.peers, msg.strokes);
        return true;
      case "join":
        this.peers.set(msg.peer.id, msg.peer);
        return false;
      case "leave":
        this.peers.delete(msg.id);
        this.cursors.delete(msg.id);
        return false;
      case "cursor":
        this.cursors.set(msg.id, { x: msg.x, y: msg.y });
        return false;
      case "cursor-leave":
        this.cursors.delete(msg.id);
        return false;
      case "start":
      case "add":
        return this.board.add(msg.stroke) !== undefined;
      case "points": {
        const stroke = this.board.get(msg.id);
        return stroke ? this.board.append(msg.id, stroke.owner, msg.points) : false;
      }
      case "end": {
        const stroke = this.board.get(msg.id);
        return stroke ? this.board.finish(msg.id, stroke.owner) !== undefined : false;
      }
      case "remove":
        if (this.current?.id === msg.id) this.current = undefined;
        return this.board.remove(msg.id);
      case "clear":
        this.current = undefined;
        this.board.clear();
        return true;
      case "pong":
        return false;
    }
  }

  /**
   * The server's snapshot replaces the local board. Then everything done offline
   * is replayed on top, locally and to the server, in the order it happened.
   */
  private welcome(you: Peer, peers: Peer[], strokes: Stroke[]): void {
    this.online = true;
    this.epoch++;
    this.me = you;
    this.peers.clear();
    this.cursors.clear();
    for (const peer of peers) this.peers.set(peer.id, peer);

    const inProgress = this.current ? this.board.get(this.current.id) : undefined;
    this.board.reset(strokes);
    const outbox = this.outbox;
    this.outbox = [];
    for (const msg of outbox) {
      if (msg.t === "add") this.board.add({ ...msg.stroke, owner: this.owner, done: true });
      else if (msg.t === "undo") this.board.undo(this.owner);
      else if (msg.t === "clear") this.board.clear();
      this.send(msg);
    }
    if (inProgress) this.board.add({ ...inProgress, points: inProgress.points.slice(), done: false });
  }

  private sendOrQueue(msg: ClientMessage): void {
    if (this.online) this.send(msg);
    else this.outbox.push(msg);
  }
}

/** Reconnect delay: 0.5s doubling to a 10s cap, with up to 30% jitter. */
export function backoffDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(10_000, 500 * 2 ** Math.max(0, attempt));
  return Math.round(base * (1 + 0.3 * random()));
}

export function randomId(length = 12): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let id = "";
  for (const byte of bytes) id += alphabet[byte % alphabet.length];
  return id;
}
